import {
  PictureTypeLabels,
  RealEstateTypeLabels,
} from "@/features/storedpictures/types/enums";
import { getPrefectureNamesByIds } from "@/utils/prefecture";
import { type AiStoredPictureSearchQuery } from "./types";

const ownerNameSelectTypeLabels = {
  startswith: "前方一致",
  endswith: "後方一致",
  contains: "部分一致",
  exact: "完全一致",
} as const;

const pictureTypeLabels = {
  SHOYUSHAJIKO: PictureTypeLabels.SHOYUSHAJIKO,
  ZENBUJIKO: PictureTypeLabels.ZENBUJIKO,
} as const;

const realEstateTypeLabels = {
  LAND: RealEstateTypeLabels.LAND,
  BUILDING: RealEstateTypeLabels.BUILDING,
  CONDOMINIUM: RealEstateTypeLabels.CONDOMINIUM,
} as const;

const prefectureLabel = (prefectureIds: number[]): string => {
  const prefectureNames = getPrefectureNamesByIds(prefectureIds);
  if (prefectureNames.length === 0) {
    return "全国";
  }
  if (prefectureNames.length === 1) {
    return prefectureNames[0];
  }
  return `${prefectureNames[0]}ほか${prefectureNames.length - 1}件`;
};

const rangeLabel = (
  lower: number | null,
  upper: number | null,
  unit: string
): string | null => {
  if (lower === null) return upper === null ? null : `${upper}${unit}以下`;
  if (upper === null) return `${lower}${unit}以上`;
  if (lower === upper) return `${lower}${unit}`;
  return `${lower}〜${upper}${unit}`;
};

export const pictureTypeLabel = (
  value: keyof typeof pictureTypeLabels
): string => pictureTypeLabels[value];

export const realEstateTypeLabel = (
  value: keyof typeof realEstateTypeLabels
): string => realEstateTypeLabels[value];

export const searchConditionLabel = (
  query: AiStoredPictureSearchQuery
): string => {
  const conditions = [prefectureLabel(query.prefectures)];
  if (query.location !== null) {
    conditions.push(`所在「${query.location}」`);
  }
  if (query.ownerName !== null) {
    conditions.push(
      `所有者名「${query.ownerName}」${
        ownerNameSelectTypeLabels[query.ownerNameSelectType]
      }`
    );
  }
  if (query.ownerAddress !== null) {
    conditions.push(`所有者住所「${query.ownerAddress}」`);
  }

  const propertyCount = rangeLabel(
    query.ownerPropertyCountFrom,
    query.ownerPropertyCountTo,
    "筆"
  );
  if (propertyCount !== null) {
    conditions.push(`保有物件数 ${propertyCount}`);
  }

  conditions.push(query.pictureTypes.map(pictureTypeLabel).join("・"));
  if (query.isEmptyHouse) {
    conditions.push("空き家または賃貸不動産のみ");
  }

  const ownershipCount = rangeLabel(query.mochibunFrom, query.mochibunTo, "人");
  if (ownershipCount === "1人") {
    conditions.push("単独所有");
  } else if (ownershipCount !== null) {
    conditions.push(`共有名義人数 ${ownershipCount}`);
  }
  conditions.push(
    query.isCurrentOwnerOnly ? "現在所有者のみ" : "過去の所有者を含む"
  );
  if (query.latestOwnerOnly) {
    conditions.push("「所有者変更の可能性」注記を除外");
  }
  return conditions.join(" / ");
};
